import React, { useState, useEffect } from 'react';
import { getUserId, getUserDetails, getUserIncomeBreakdown, getUserActivities } from '../utils/contract';
import { fromWei } from '../utils/web3';
import './Record.css';

const CORE_NAMES = ['NeuroCore', 'AlgoCore', 'IntelliCore', 'SynapCore', 'DeepCore', 'DataCore', 'MindCore', 'QuantumCore'];

function Record({ account }) {
  const [userDetails, setUserDetails] = useState(null);
  const [incomeBreakdown, setIncomeBreakdown] = useState(null);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingActivities, setLoadingActivities] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  useEffect(() => {
    if (account) {
      loadRecordData();
    }
  }, [account]);

  const loadRecordData = async () => {
    setLoading(true);
    try {
      const userId = await getUserId(account);

      if (userId && userId !== '0') {
        const [details, breakdown] = await Promise.all([
          getUserDetails(userId),
          getUserIncomeBreakdown(userId)
        ]);

        setUserDetails(details);
        setIncomeBreakdown(breakdown);

        loadActivities(userId);
      }
    } catch (error) {
      console.error('Error loading record data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadActivities = async (userId) => {
    setLoadingActivities(true);
    try {
      const userActivities = await getUserActivities(userId);
      setActivities(userActivities || []);
      setCurrentPage(1);
    } catch (error) {
      console.error('Error loading activities:', error);
      setActivities([]);
    } finally {
      setLoadingActivities(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown';
    const date = new Date(timestamp * 1000);
    return date.toLocaleString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const getActivityIcon = (type) => {
    const icons = {
      registration: '🎉', activation: '🚀', retopup: '🔄',
      direct_income: '💰', matrix_income: '🎯', normal_income: '📊',
      claim: '💸', pool_claim: '🏊', royalty_claim: '👑'
    };
    return icons[type] || '📝';
  };

  const getActivityColor = (type) => {
    const colors = {
      registration: '#22d3ee', activation: '#22d3ee', retopup: '#34f5c5',
      direct_income: '#34f5c5', matrix_income: '#22d3ee', normal_income: '#a855f7',
      claim: '#34f5c5', pool_claim: '#6366f1', royalty_claim: '#fbbf24'
    };
    return colors[type] || 'rgba(255,255,255,.7)';
  };

  const getActivityTitle = (activity) => {
    const { type, packageId, fromId, level } = activity;
    const coreName = packageId && packageId <= 8 ? CORE_NAMES[packageId - 1] : '';

    switch (type) {
      case 'registration': return `Joined Smartcore Network`;
      case 'activation': return `Activated ${coreName}`;
      case 'retopup': return `Retopped ${coreName}`;
      case 'direct_income': return `Direct Income from User #${fromId || 'N/A'}`;
      case 'matrix_income': return `Matrix Income (Level ${level || 'N/A'}) from User #${fromId || 'N/A'}`;
      case 'normal_income': return `Generation Income (Level ${level || 'N/A'}) from User #${fromId || 'N/A'}`;
      case 'claim': return 'Income Claimed';
      case 'pool_claim': return `Pool Income Claimed - ${coreName}`;
      case 'royalty_claim': return 'Royalty Income Claimed';
      default: return 'Activity';
    }
  };

  if (loading) {
    return (
      <div className="container">
        <div className="loading-screen">
          <img src="/t1.gif" alt="Loading" style={{ width: '120px', height: '120px' }} />
          <div className="loading"></div>
          <div className="loading-text">Loading analytics...</div>
        </div>
      </div>
    );
  }

  if (!userDetails || !incomeBreakdown) {
    return (
      <div className="container">
        <div className="r-empty">
          <div className="r-empty-icon">📊</div>
          <h2>No Data Available</h2>
          <p>Please register first to view your analytics</p>
        </div>
      </div>
    );
  }

  const summary = [
    { label: 'Total Income', value: fromWei(incomeBreakdown.totalIncome), accent: '#22d3ee' },
    { label: 'Claimable', value: fromWei(incomeBreakdown.claimableAmount), accent: '#34f5c5' },
    { label: 'Total Claimed', value: fromWei(incomeBreakdown.totalClaimed), accent: '#a855f7' },
  ];

  const breakdown = [
    { label: 'Direct Income', value: fromWei(incomeBreakdown.directReferralIncome), color: '#34f5c5', icon: '💰' },
    { label: 'Matrix Income', value: fromWei(incomeBreakdown.matrixLevelIncome), color: '#22d3ee', icon: '🎯' },
    { label: 'Generation Income', value: fromWei(incomeBreakdown.normalLevelIncome), color: '#a855f7', icon: '🌐' },
    { label: 'Pool Income', value: fromWei(incomeBreakdown.poolIncome), color: '#6366f1', icon: '🏊' },
    { label: 'Royalty Income', value: fromWei(incomeBreakdown.royaltyIncome), color: '#fbbf24', icon: '👑', full: true },
  ];

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentActivities = activities.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(activities.length / itemsPerPage);
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  return (
    <div className="container record">
      <header className="page-hero">
        <div className="page-hero-text">
          <span className="page-hero-eyebrow">Analytics</span>
          <h1 className="page-hero-title">Analytics Dashboard</h1>
          <p className="page-hero-sub">Track your income and network activities</p>
        </div>
        <div className="page-hero-id">
          <span>Core ID</span>
          <b>#{userDetails.id}</b>
        </div>
      </header>

      {/* Income summary */}
      <div className="r-summary">
        {summary.map((s) => (
          <div className="r-sum" key={s.label} style={{ '--sa': s.accent }}>
            <span className="r-sum-label">{s.label}</span>
            <span className="r-sum-value">{s.value}</span>
            <span className="r-sum-unit">USDT</span>
          </div>
        ))}
      </div>

      {/* Income breakdown */}
      <div className="r-panel">
        <h3 className="r-panel-title">📈 Income Breakdown by Type</h3>
        <div className="r-breakdown">
          {breakdown.map((b) => (
            <div className={`r-break ${b.full ? 'full' : ''}`} key={b.label} style={{ '--ba': b.color }}>
              <div className="r-break-info">
                <span className="r-break-label">{b.label}</span>
                <span className="r-break-value">{b.value} <small>USDT</small></span>
              </div>
              <span className="r-break-icon">{b.icon}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Activity log */}
      <div className="r-panel">
        <h3 className="r-panel-title">📜 Activity Log <span className="r-count">{activities.length}</span></h3>
        {loadingActivities ? (
          <div className="r-act-loading"><div className="loading"></div><p>Loading activities...</p></div>
        ) : activities.length === 0 ? (
          <div className="r-empty-inline"><div className="r-empty-icon">📝</div><p>No activities yet</p><small>Your transaction history will appear here</small></div>
        ) : (
          <>
            <div className="r-timeline">
              {currentActivities.map((activity, idx) => (
                <div className="r-act" key={idx} style={{ '--aa': getActivityColor(activity.type) }}>
                  <span className="r-act-icon">{getActivityIcon(activity.type)}</span>
                  <div className="r-act-body">
                    <span className="r-act-title">{getActivityTitle(activity)}</span>
                    <span className="r-act-date">{formatDate(activity.timestamp)}</span>
                  </div>
                  {activity.amount && parseFloat(fromWei(activity.amount)) > 0 && (
                    <span className="r-act-amount">+{fromWei(activity.amount)}</span>
                  )}
                </div>
              ))}
            </div>

            {totalPages > 1 && (
              <div className="r-pager">
                <button className="r-pager-btn" onClick={() => paginate(currentPage - 1)} disabled={currentPage === 1}>← Prev</button>
                <span className="r-pager-info">Page {currentPage} of {totalPages}</span>
                <button className="r-pager-btn" onClick={() => paginate(currentPage + 1)} disabled={currentPage === totalPages}>Next →</button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default Record;
